import { Link, router } from "@inertiajs/react";
import { ChevronDown, ChevronUp, ExternalLink, Package } from "lucide-react";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Candidate, Field, fmt, PackageSummary, STAGE_LABELS, SOURCE_LABELS } from "./_types";

const PKG_STATUS_COLORS: Record<string, string> = {
  not_started:      "bg-gray-100 text-gray-500",
  generating:       "bg-blue-100 text-blue-600",
  ready_for_review: "bg-yellow-100 text-yellow-700",
  approved:         "bg-green-100 text-green-700",
  executing:        "bg-purple-100 text-purple-700",
  completed:        "bg-green-200 text-green-800",
  failed:           "bg-red-100 text-red-600",
};

interface Props {
  candidate: Candidate;
  bgOpen: boolean;
  setBgOpen: (open: (prev: boolean) => boolean) => void;
  package_summary: PackageSummary | null;
}

export default function OverviewTab({ candidate, bgOpen, setBgOpen, package_summary }: Props) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-5">
        <Card>
          <CardHeader><CardTitle className="text-base">Contact</CardTitle></CardHeader>
          <CardContent>
            <dl className="grid grid-cols-2 gap-4">
              <Field label="Email" value={candidate.email} />
              <Field label="Phone" value={candidate.phone} />
              {candidate.linkedin_url && (
                <div>
                  <dt className="text-xs text-gray-500 uppercase tracking-wide">LinkedIn</dt>
                  <dd className="mt-0.5">
                    <a href={candidate.linkedin_url} target="_blank" rel="noreferrer"
                      className="text-sm text-blue-600 hover:underline inline-flex items-center gap-1">
                      Profile <ExternalLink className="w-3 h-3" />
                    </a>
                  </dd>
                </div>
              )}
              {candidate.profile_url && (
                <div>
                  <dt className="text-xs text-gray-500 uppercase tracking-wide">Leopard Profile</dt>
                  <dd className="mt-0.5">
                    <a href={candidate.profile_url} target="_blank" rel="noreferrer"
                      className="text-sm text-blue-600 hover:underline inline-flex items-center gap-1">
                      View <ExternalLink className="w-3 h-3" />
                    </a>
                  </dd>
                </div>
              )}
            </dl>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Education &amp; Background</CardTitle></CardHeader>
          <CardContent>
            <dl className="grid grid-cols-2 gap-4">
              <Field label="Law School" value={candidate.law_school} />
              <Field label="Undergrad" value={candidate.undergrad} />
              <Field label="JD Year" value={candidate.jd_year} />
              <Field label="GPA" value={candidate.gpa} />
              <Field label="Degrees" value={candidate.degrees} />
              <Field label="Honors" value={candidate.honors} />
              <Field label="Admitted" value={candidate.admitted} />
              <Field label="All Admissions" value={candidate.all_admissions} />
            </dl>
          </CardContent>
        </Card>

        <Card>
          <button
            className="w-full flex items-center justify-between px-6 py-4 text-left"
            onClick={() => setBgOpen((o) => !o)}
          >
            <span className="text-sm font-semibold text-gray-800">Professional Details</span>
            {bgOpen ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
          </button>
          {bgOpen && (
            <CardContent className="pt-0">
              <dl className="grid grid-cols-2 gap-4">
                <Field label="Firm" value={candidate.firm_name} />
                <Field label="Type" value={candidate.candidate_type} />
                <Field label="Practice Area" value={candidate.practice_area} />
                <Field label="Specialties" value={candidate.specialties} />
                <Field label="Location" value={candidate.location} />
                <Field label="Languages" value={candidate.languages} />
                <Field label="Industries" value={candidate.industries} />
                <Field label="Origin Firm" value={candidate.origin_firm} />
                <Field label="Source" value={SOURCE_LABELS[candidate.source] ?? candidate.source} />
              </dl>
            </CardContent>
          )}
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Intake Notes (IBI)</CardTitle></CardHeader>
          <CardContent>
            {candidate.notes
              ? <p className="text-sm text-gray-700 whitespace-pre-wrap">{candidate.notes}</p>
              : <p className="text-sm text-gray-400 italic">No intake notes yet.</p>
            }
          </CardContent>
        </Card>
      </div>

      <div className="space-y-5">
        <Card>
          <CardHeader><CardTitle className="text-base">Pipeline</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            <div className="flex items-center justify-between">
              <Badge variant="outline">{STAGE_LABELS[candidate.pipeline_stage] ?? candidate.pipeline_stage}</Badge>
              <span className="text-xs text-gray-500">{candidate.days_in_stage}d in stage</span>
            </div>
            <div className="text-xs text-gray-500">Source: {SOURCE_LABELS[candidate.source] ?? candidate.source}</div>
            {candidate.intake_date && (
              <div className="text-xs text-gray-500">Intake: {fmt(candidate.intake_date)}</div>
            )}
            <div className="text-xs text-gray-400">Added {fmt(candidate.created_at)}</div>
          </CardContent>
        </Card>

        {/* Package card */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-1.5">
                <Package className="w-4 h-4 text-gray-400" /> Package
              </CardTitle>
              <Link href={`/candidates/${candidate.id}/package`} className="text-xs text-blue-600 hover:underline">
                {package_summary ? "View" : "Create"}
              </Link>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {package_summary ? (
              <>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${PKG_STATUS_COLORS[package_summary.status] ?? "bg-gray-100 text-gray-500"}`}>
                  {package_summary.status.replace(/_/g, " ")}
                </span>
                <div className="text-xs text-gray-500 space-y-0.5">
                  <div>{package_summary.submissions_count} draft submission{package_summary.submissions_count !== 1 ? "s" : ""}</div>
                  <div>{package_summary.skm_contacts_count} SKM contact{package_summary.skm_contacts_count !== 1 ? "s" : ""}</div>
                </div>
                {(package_summary.status === "not_started" || package_summary.status === "failed") && (
                  <Button variant="outline" size="sm" className="w-full text-xs"
                    onClick={() => router.post(`/candidates/${candidate.id}/package/generate`)}>
                    Generate Package
                  </Button>
                )}
              </>
            ) : (
              <p className="text-xs text-gray-400">No package yet.</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
